import { ncbClient } from './ncbClient';
import dropboxService from './dropboxService';

export const productService = {
  async getProducts(params = {}) {
    try {
      console.log('Fetching products with params:', params);
      const response = await ncbClient.readProducts(params);
      if (!response?.data) {
        console.warn('No products data found in response');
        return [];
      }
      return response.data.map(product => ({
        id: product.product_id,
        name: product.product_name || 'Unnamed Product',
        description: product.description || 'No description available',
        price: parseFloat(product.price) || 0,
        stock: parseInt(product.stock) || 0,
        categoryId: product.category_id,
        category: `Category ${product.category_id}`,
        image: product.image_url || `https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&h=200&fit=crop&${product.product_id}`,
        rating: Math.random() * 5, // Mock rating since not in schema
        reviews: Math.floor(Math.random() * 100), // Mock reviews
        featured: Math.random() > 0.7, // Mock featured flag
        sale: Math.random() > 0.6, // Mock sale flag
        originalPrice: product.price * (Math.random() > 0.5 ? 1.2 : 1), // Mock original price
      }));
    } catch (error) {
      console.error('Failed to fetch products:', error);
      // Return mock data for development
      if (import.meta.env.DEV) {
        return [
          {
            id: 1,
            name: 'Wireless Headphones',
            description: 'High-quality wireless headphones with noise cancellation',
            price: 99.99,
            stock: 15,
            categoryId: 1,
            category: 'Electronics',
            image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=300&h=200&fit=crop',
            rating: 4.5,
            reviews: 128,
            featured: true,
            sale: false,
            originalPrice: 129.99
          },
          {
            id: 2,
            name: 'Smart Watch',
            description: 'Feature-rich smartwatch with health monitoring',
            price: 199.99,
            stock: 8,
            categoryId: 1,
            category: 'Electronics',
            image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=300&h=200&fit=crop',
            rating: 4.2,
            reviews: 86,
            featured: true,
            sale: true,
            originalPrice: 249.99
          }
        ];
      }
      throw new Error(`Failed to fetch products: ${error.response?.data?.message || error.message}`);
    }
  },

  async getProduct(id) {
    try {
      console.log(`Fetching product with ID: ${id}`);
      const response = await ncbClient.readProduct(id);
      if (!response?.data) {
        throw new Error('Product not found');
      }
      const product = response.data;
      return {
        id: product.product_id,
        name: product.product_name || 'Unnamed Product',
        description: product.description || 'No description available',
        price: parseFloat(product.price) || 0,
        stock: parseInt(product.stock) || 0,
        categoryId: product.category_id,
        category: `Category ${product.category_id}`,
        image: product.image_url || `https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=600&h=400&fit=crop&${product.product_id}`,
        images: [
          product.image_url || `https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=600&h=400&fit=crop&${product.product_id}`,
          `https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600&h=400&fit=crop&${product.product_id}`,
          `https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&h=400&fit=crop&${product.product_id}`,
        ],
        rating: Math.random() * 5,
        reviews: Math.floor(Math.random() * 100),
        featured: Math.random() > 0.7,
        sale: Math.random() > 0.6,
        originalPrice: (parseFloat(product.price) || 0) * (Math.random() > 0.5 ? 1.2 : 1),
        specifications: {
          'Material': 'Premium Quality',
          'Dimensions': 'Standard Size',
          'Weight': 'Lightweight',
          'Warranty': '1 Year',
          'Brand': 'Premium Brand',
        },
      };
    } catch (error) {
      console.error(`Failed to fetch product ${id}:`, error);
      // Return mock data for development
      if (import.meta.env.DEV) {
        return {
          id: id,
          name: 'Sample Product',
          description: 'This is a sample product description for development purposes.',
          price: 49.99,
          stock: 10,
          categoryId: 1,
          category: 'Electronics',
          image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=600&h=400&fit=crop',
          images: [
            'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=600&h=400&fit=crop',
            'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600&h=400&fit=crop',
            'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=600&h=400&fit=crop',
          ],
          rating: 4.3,
          reviews: 54,
          featured: true,
          sale: false,
          originalPrice: 59.99,
          specifications: {
            'Material': 'Premium Quality',
            'Dimensions': 'Standard Size',
            'Weight': 'Lightweight',
            'Warranty': '1 Year',
            'Brand': 'Premium Brand',
          },
        };
      }
      throw new Error(`Failed to fetch product: ${error.response?.data?.message || error.message}`);
    }
  },

  async createProduct(productData) {
    try {
      console.log('Creating product:', productData);

      let imageUrl = productData.image || '';

      if (productData.imageFile && dropboxService.isConfigured()) {
        try {
          const uploadedImage = await dropboxService.uploadFile(
            productData.imageFile,
            `/products/${Date.now()}-${productData.imageFile.name}`
          );
          imageUrl = uploadedImage.shared_link;
          console.log('Product image uploaded to Dropbox:', imageUrl);
        } catch (uploadError) {
          console.warn('Failed to upload image to Dropbox, using fallback:', uploadError);
        }
      }

      const response = await ncbClient.createProduct({
        product_name: productData.name,
        description: productData.description,
        price: Number(productData.price),
        stock: Number.isFinite(Number(productData.stock)) ? Number(productData.stock) : 0,
        category_id: Number(productData.categoryId),
        ...(imageUrl ? { image_url: imageUrl } : {}),
      });

      console.log('Product created successfully:', response);
      return response;
    } catch (error) {
      console.error('Failed to create product:', error);
      throw new Error(
        error?.response?.data?.message ||
        error?.message ||
        'Failed to create product'
      );
    }
  },

  async updateProduct(id, productData) {
    try {
      console.log(`Updating product ${id}:`, productData);

      let imageUrl = productData.image || '';

      if (productData.imageFile && dropboxService.isConfigured()) {
        try {
          const uploadedImage = await dropboxService.uploadFile(
            productData.imageFile,
            `/products/${Date.now()}-${productData.imageFile.name}`
          );
          imageUrl = uploadedImage.shared_link;
          console.log('Updated product image uploaded to Dropbox:', imageUrl);
        } catch (uploadError) {
          console.warn('Failed to upload updated image to Dropbox:', uploadError);
        }
      }

      const response = await ncbClient.updateProduct(id, {
        product_name: productData.name,
        description: productData.description,
        price: Number(productData.price),
        stock: Number.isFinite(Number(productData.stock)) ? Number(productData.stock) : 0,
        category_id: Number(productData.categoryId),
        ...(imageUrl ? { image_url: imageUrl } : {}),
      });

      console.log('Product updated successfully:', response);
      return response;
    } catch (error) {
      console.error(`Failed to update product ${id}:`, error);
      throw new Error(
        error?.response?.data?.message ||
        error?.message ||
        'Failed to update product'
      );
    }
  },

  async deleteProduct(id) {
    try {
      console.log(`Deleting product ${id}`);
      await ncbClient.deleteProduct(id);
      console.log('Product deleted successfully');
      return true;
    } catch (error) {
      console.error(`Failed to delete product ${id}:`, error);
      throw new Error(`Failed to delete product: ${error.response?.data?.message || error.message}`);
    }
  },

  async getCategories() {
    try {
      console.log('Fetching categories');
      const response = await ncbClient.readCategories();
      if (!response?.data) {
        console.warn('No categories data found in response');
        return [];
      }
      return response.data.map(category => ({
        id: category.category_id,
        name: category.category_name || 'Unnamed Category',
        parentId: category.parent_id,
        slug: category.category_name?.toLowerCase().replace(/\s+/g, '-') || 'uncategorized',
        description: `Products in ${category.category_name || 'this category'}`,
        image: `https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=300&h=200&fit=crop&${category.category_id}`,
        isActive: true,
      }));
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      // Return default categories if API fails
      return [
        { id: 1, name: 'Electronics', slug: 'electronics', description: 'Electronic devices and gadgets' },
        { id: 2, name: 'Clothing', slug: 'clothing', description: 'Fashion and apparel' },
        { id: 3, name: 'Home & Garden', slug: 'home-garden', description: 'Home improvement and garden supplies' },
      ];
    }
  },
};